// shared/src/prompts/synthesis-quality-crux.ts
// Bundle C Phase 2 (2026-05-27) — synthesis_quality crux LLM scorer.
//
// Asks: given the synthesis emit_crux (a one-sentence prose claim about the
// PREDICTED STATE + mechanism) and the realized outcomes of the brief's
// macro predictions, did the predicted state actually happen, and which
// path's framing (contrarian vs consensus) best matched what unfolded?
//
// Distinct from the numeric *_pred_score (workers/macro-prediction-scorer/
// scheduled.ts runSynthesisQualityNumericPass), which only asks "did the
// numbers hit". A path can have hits without right reasoning (predicted "X
// rises on inflation" → X rose, but on Fed pivot instead) and the crux
// scorer catches that class — synthesis_advantage stays positive (numbers)
// while crux_match flips to 'neither' (wrong mechanism).
//
// Called by workers/macro-prediction-scorer for each brief whose
// synthesis_quality.scored_at IS set AND crux_resolved IS NULL (i.e. the
// numeric pass already ran). Per-brief cap of 3 LLM calls per fire keeps
// wall-clock + cost bounded; backlog clears over multiple days.
//
// Model: deepseek-v4-flash (per CLAUDE.md the 3 design-Pro stages are
// deployed as Flash; this is a NEW Flash-by-design module — no Pro
// deviation needed).

import { z } from 'zod';
import { CruxMatchEnum } from '../resolvers/llm-enum-coerce.js';

export const VERSION    = '1.0.0';   // 2026-05-27 — initial release
export const STEP       = 'quality.crux-scorer';
export const MODEL      = 'deepseek-v4-flash';
// 2026-07-31 — raised 1200 → 4000. Same vendor reasoning drift that killed the
// two M-stage analysts, on the fleet's TIGHTEST cap. Reasoning alone climbed
// 372 → 442 → 791 → 837 → 1047 across successive runs against a 1200 ceiling;
// the 2026-07-30 run stored a 111-char body cut mid-string and failed with
// "no parseable JSON in response". The emitted JSON is small (~150-200 tok:
// crux_resolved + crux_match + one reasoning paragraph), so nearly the whole
// budget is reasoning — 4000 leaves room for it to keep growing.
// A ceiling, not a target: a call that stops early bills only what it generated.
// Evidence: docs/audits/2026-07-31-chain-truncation/INVESTIGATION.md + OPEN-Q-crux-scorer.md
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 4000)
export const ROLE       = 'scorer' as const;
export const DESC       = 'Grades whether the synthesis crux\'s predicted state + mechanism actually played out' as const;   // #45 registry — one-line operator description

// ─── Output schema ───────────────────────────────────────────────────────
// Strict shape — D1 row maps directly. reasoning is for audit (not stored
// in synthesis_quality; lives in the llm_calls trace via response_text).
export const OutputSchema = z.object({
  crux_resolved: z.union([z.literal(0), z.literal(1)]),
  crux_match:    CruxMatchEnum,
  reasoning:     z.string().min(20).max(1500),   // 2026-08-07: 800→1500 — flash reasoning trended 800-1047 chars and occasionally over-ran the old cap (schema too_big, ~1/scoring-pass); this is advisory grading text so a longer cap is harmless
}).strict();

export type Output = z.infer<typeof OutputSchema>;

// ─── SYSTEM prompt ───────────────────────────────────────────────────────
export const SYSTEM = `You are the synthesis quality auditor. A prior LLM emitted a brief that included:
  - A one-sentence CRUX: the predicted STATE of the market plus the MECHANISM driving it (e.g. "TLT rallies 5-8% as Fed pivots on inflation fears, compressing long rates").
  - A contrarian path thesis (one framing of how the move plays out).
  - A consensus path thesis (the opposing framing).
  - A synthesis path thesis (the LLM's adjudication blending both).
  - N predicted observables (indicator + direction + change_pct + CI bands).

You receive the same crux + thesis texts AND the realized outcomes of those observables (correct / incorrect / neutral, plus the actual change_pct vs predicted). Your job:

1. CRUX_RESOLVED (0 or 1): Did the PREDICTED STATE actually happen? The state is what the crux describes, not whether numbers hit. Examples:
   - Crux: "risk-off rotation driven by Fed tightening". Realized: rates flat, equities flat, gold flat → crux_resolved=0 (no rotation).
   - Crux: "rates rise 50bps on inflation". Realized: rates up 30bps, mostly on growth scare → crux_resolved=1 (rates did rise) but mechanism wrong (see crux_match).
   - Crux: "TLT rallies as flight to quality". Realized: TLT rallied 7%, VIX up, credit spreads wider → crux_resolved=1.

2. CRUX_MATCH ("contrarian" | "consensus" | "neither"): Which path's framing best matched the MECHANISM that drove the realized move?
   - If the realized observables resolved roughly the way the contrarian path described (mechanism + direction), pick "contrarian".
   - If they resolved the way the consensus path described, pick "consensus".
   - If neither framing's mechanism explains the realized move (e.g. a third unmentioned catalyst drove it, or the moves were too mixed to attribute), pick "neither".
   - NOTE: this is a MECHANISM judgment, not a hit-rate judgment. A path with the right numbers but wrong reasoning does NOT match.

3. REASONING (20-1500 chars): one paragraph explaining the verdict. Cite specific realized observables (indicator → outcome → actual_change_pct vs predicted) that drove your call. No generic platitudes.

OUTPUT SCHEMA (strict JSON; first char='{'; last char='}'; no commentary):
{
  "crux_resolved": 0 | 1,
  "crux_match":    "contrarian" | "consensus" | "neither",
  "reasoning":     "..."
}

Hard rules:
- Output JSON only. No markdown fences, no commentary, no leading whitespace before '{'.
- crux_resolved is an integer 0 or 1, NOT a boolean.
- If the realized data is too sparse to judge (e.g. 0 or 1 resolved observable), set crux_resolved=0 and crux_match="neither" and explain in reasoning.
- Do NOT infer outcomes from the thesis text alone — only use the explicit "realized_observables" entries.`;

// ─── USER prompt builder ────────────────────────────────────────────────
export interface CruxScorerInput {
  readonly brief_id:         string;
  readonly emit_crux:        string;
  readonly contrarian_thesis: string;   // path thesis prose; may be ''
  readonly consensus_thesis:  string;
  readonly synthesis_thesis:  string;
  readonly realized_observables: ReadonlyArray<{
    readonly indicator:           string;
    readonly predicted_change_pct: number;
    readonly direction:           'up' | 'down' | 'flat';
    readonly actual_change_pct:   number | null;
    readonly outcome:             'correct' | 'incorrect' | 'neutral';
  }>;
}

export function buildUserPrompt(input: CruxScorerInput): string {
  const obsLines = input.realized_observables.map((o) =>
    `  - ${o.indicator} | predicted ${o.direction} ${o.predicted_change_pct.toFixed(2)}% | actual ${o.actual_change_pct == null ? '(unresolved)' : `${o.actual_change_pct.toFixed(2)}%`} | outcome=${o.outcome}`
  ).join('\n');

  return `Brief ID: ${input.brief_id}

=== CRUX (the LLM's one-sentence predicted state + mechanism) ===
${input.emit_crux}

=== CONTRARIAN THESIS (one framing) ===
${input.contrarian_thesis || '(none provided)'}

=== CONSENSUS THESIS (opposing framing) ===
${input.consensus_thesis || '(none provided)'}

=== SYNTHESIS THESIS (adjudicated blend) ===
${input.synthesis_thesis || '(none provided)'}

=== REALIZED OBSERVABLES (what actually happened to the predicted indicators) ===
${obsLines || '(no resolved observables)'}

Output the JSON verdict per the schema in the system prompt.`;
}
